<template>
  <div class="flex flex-col gap-2.5">
    <h2 class="text-sm font-semibold mb-1.5">Create Faculties</h2>
    <div class="flex flex-col bg-primary/10 p-1.5 rounded">
      <div class="flex items-start gap-1.5">
        <i class="pi pi-info-circle text-primary mt-1 text-xs"></i>
        <span class="text-xs">
          Enter faculty name, click "Add" to add to the list. You can add multiple faculties, then click "Submit All" to
          save.
        </span>
      </div>
    </div>
    <form @submit.prevent="addFaculty" class="flex gap-1.5 items-end">
      <div class="flex-1 flex flex-col gap-1.5">
        <label class="font-medium text-xs">Faculty Name</label>
        <InputText v-model="facultyName" placeholder="Enter faculty name" class="w-full" />
      </div>
      <div class="flex flex-col gap-1.5" style="min-width: 9rem">
        <label class="font-medium text-xs">Type</label>
        <Select v-model="facultyType" :options="typeOptions" optionLabel="label" optionValue="value" class="w-full" />
      </div>
      <Button type="submit" label="Add" icon="pi pi-plus" size="small" :disabled="!facultyName.trim()"
        class="bg-primary" />
    </form>

    <ul v-if="faculties.length" class="flex flex-col gap-1.5 mb-1.5">
      <li v-for="(item, idx) in faculties" :key="idx"
        class="flex items-center justify-between bg-gray-100 px-2.5 py-1.5 rounded">
        <div class="flex items-center gap-2">
          <span class="text-sm">{{ item.name }}</span>
          <span class="text-xs px-2 py-0.5 rounded-full" :class="item.type === 'academic' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'">
            {{ item.type === 'academic' ? 'Academic' : 'Non-Teaching' }}
          </span>
        </div>
        <Button icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm" @click="removeFaculty(idx)" />
      </li>
    </ul>

    <div class="flex justify-end">
      <Button @click="submitAll" :label="isSubmitting ? 'Submitting...' : 'Submit All Faculties'"
        size="small" :loading="isSubmitting" :disabled="faculties.length === 0" class="!bg-primary !border-primary" />
    </div>

  </div>
</template>

<script setup lang="ts">
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';

defineProps<{
  currentStep: number
}>();

const emit = defineEmits<{
  (_e: 'update-step', _step: number): void
}>();

const { createTertiaryFaculty } = useTertiaryMutations();
const facultyName = ref('');
const facultyType = ref('academic');
const faculties = ref<{ name: string; type: string }[]>([]);
const isSubmitting = ref(false);

const typeOptions = [
  { label: 'Academic', value: 'academic' },
  { label: 'Non-Teaching', value: 'non_teaching' },
];

function addFaculty() {
  if (facultyName.value.trim()) {
    faculties.value.push({ name: facultyName.value.trim(), type: facultyType.value });
    facultyName.value = '';
  }
}

function removeFaculty(idx: number) {
  faculties.value.splice(idx, 1);
}

async function submitAll() {
  isSubmitting.value = true;

  for (const item of faculties.value) {
    try {
      await createTertiaryFaculty({ faculty_name: item.name, type: item.type });
      emit('update-step', 5);
    } catch {
      // Skip the failed faculty and continue with the rest.
    }
  }
  isSubmitting.value = false;

}
</script>
